import { DateFormat } from '@lyearn/core/utils/date/formatDate';
import { ActionMenuItemType, ActionMenuProps } from '@lyearn/molecules';
import { CalendarProps, TypographyColors, TypographyVariants } from '@lyearn/ui';
import { CreateActionItemInput, RelatedEntityTypeEnum, UserType } from '@/types/schema';

import { ActionItemFragment } from '../graphql/fragments/generated/ActionItem';
import type useActionItemActions from '../hooks/useActionItemActions';

export enum ActionItemActions {
  Duplicate = 'DUPLICATE',
  Delete = 'DELETE',
  CopyLink = 'COPY_LINK',
  Add = 'ADD',
  ADD_MULTIPLE = 'ADD_MULTIPLE',
  Toggle = 'TOGGLE',
  UpdateText = 'UPDATE_TEXT',
  UpdateDate = 'UPDATE_DATE',
  UpdateAssignedTo = 'UPDATE_ASSIGNED_TO',
  UpdateAssignedToMultiple = 'UPDATE_ASSIGNED_TO_MULTIPLE',
  UpdatePriority = 'UPDATE_PRIORITY',
  UpdateStatus = 'UPDATE_STATUS',
  UpdateOrder = 'UPDATE_ORDER',
  UpdateFile = 'UPDATE_FILE',
  UpdateStage = 'UPDATE_STAGE',
}

export type ActionItemOnActionType = {
  action: ActionItemActions;
  data: Partial<ActionItemFragment> & { id: string; actionItems?: CreateActionItemInput[] };
};

export interface ActionItemType {
  actionItem: ActionItemFragment;
  entityType: RelatedEntityTypeEnum;
  position: number;
  actions: ReturnType<typeof useActionItemActions>['actions'];
}

export type ActionDueDatePickerProps = {
  className?: string;
  fixedWidth?: boolean; // use fixed width when not redering inside a table
  shouldDisableDate?: CalendarProps['shouldDisableDate'];
  format?: DateFormat;
};

export type ActionCheckBoxProps = {
  disabled?: boolean;
  size?: 'small' | 'medium';
  className?: string;
};

export type ActionOptionsProps = {
  entityType?: RelatedEntityTypeEnum;
  overrides?: ActionMenuProps['overrides'];
  useMoreVertical?: boolean;
  actionMenu?: {
    items: ActionMenuItemType[];
    onAction: (task: ActionItemFragment) => (val: string) => void;
  };
};

export type ActionInputProps = {
  position: number;
  autoFocus?: boolean;
  className?: string;
  variant?: TypographyVariants;
};

export type AssignMenuProps = {
  groupIds?: string[];
  profileSettings?: {
    showName?: boolean;
    classes?: { name?: string; color?: TypographyColors };
    showAssignedToTooltip?: boolean;
  };
  className?: string;
  fixedWidth?: boolean;
};

export type ActionPriorityProps = {
  className?: string;
  hideLabel?: boolean;
};

export type ActionStatusProps = {
  size?: number;
  className?: string;
};

export type ActionStageProps = {
  size?: number;
  className?: string;
};

export type TaskProviderStatusProps = {
  className?: string;
};

export type TaskListActionsProps = {
  className?: string;
  fetchMore?: () => void;
  isFetchingMore?: boolean;
  onAddActionItem?: () => void;
  entity?: RelatedEntityTypeEnum;
  variant?: TypographyVariants;
  iconSize?: number;
  showAISuggestions?: () => void;
};

export type ActionNameProps = {
  className?: string;
  color?: TypographyColors;
  numberOfLines?: number;
  variant?: TypographyVariants;
  isRenameMode?: boolean;
  disableRenameMode?: () => void;
};

export type CreatedOnProps = {
  className?: string;
};

export type StaticAssignMenuProps = {
  participants: UserType[];
  className?: string;
  fixedWidth?: boolean; // use fixed width when not redering inside a table
};

export enum Status {
  TODO = 'todo',
  COMPLETED = 'completed',
}

export type ActionIdentifierProps = {
  name?: string | null;
  className?: string;
  color?: TypographyColors;
  numberOfLines?: number;
  variant?: TypographyVariants;
};
